<template>
    <section class="bg-slate-50/60 my-4">
        <div class="mx-auto mb-20 lg:mb-0 lg:p-20">
            <div class="grid items-start gap-12 lg:grid-cols-2">
                <!-- Left copy -->
                <div>
                    <h6 v-if="subheading" class="text-blue-700 font-semibold tracking-wide uppercase text-xs">{{ subheading }}</h6>
                    <h1 class="font-extrabold">{{ heading }}</h1>

                    <ul class="mt-8 space-y-4">
                        <FeatureItem v-for="(feature, idx) in features" :key="idx" :title="feature.title" :desc="feature.desc" />
                    </ul>
                </div>

                <!-- Right pricing card -->
                <div id="pricing-card">
                    <div class="overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-slate-200">
                        <!-- Header -->
                        <div class="flex flex-col items-center gap-3 border-b border-slate-100 px-8 py-8 text-center">
                            <img class="w-20 h-auto object-cover bg-slate-100 rounded-full" :src="productGroup.image" />
                            <div class="space-y-1">
                                <h3 class="text-lg font-semibold">{{ pricingTitle }}</h3>
                                <p class="text-sm text-slate-500">{{ pricingSubtitle }}</p>
                            </div>
                        </div>

                        <!-- Price options -->
                        <ul v-for="productVariant in sortedProductVariants" :key="productVariant.id" class="divide-y divide-slate-100">
                            <PriceRow
                                :label="$translate(getAttributeByType(productVariant, 'period').title)"
                                :badge="productVariant.discount_percent * 100 === 10 ? 'Beliebt' : ''"
                                :note="$t('per_month')"
                                :sub="productVariant.discount_percent > 0 ? '-' + $toPercent(productVariant.discount_percent, 0) : ''"
                                :price="$toCurrency(productVariant.base_price_gross - productVariant.base_price_gross * productVariant.discount_percent)"
                                :class="selectedProductVariant === productVariant ? 'bg-blue-dark text-white' : ''"
                                @click="$emit('select-product', productVariant)"
                            />
                        </ul>

                        <!-- Webinar Toggle -->
                        <div v-if="hasWebinar()" class="px-6 py-4">
                            <div class="text-right">
                                <info-text class="" :title="$t('webinar_option_explanation_altcoin.title')" :slots="true">
                                    <h5 class="font-bold text-sm">{{ $t('webinar_option_explanation_altcoin.title') }}</h5>
                                    <span class="text-black text-sm"> {{ $t('webinar_option_explanation_altcoin.text') }}</span>
                                </info-text>
                            </div>
                            <div class="bg-purple-300 rounded-lg px-4 py-2">
                                <div class="flex items-center justify-between w-full cursor-pointer">
                                    <span class="ext-sm text-purple-500 w-full">
                                        <div>
                                            <i class="fi fi-rr-video-camera mr-2" />
                                            <span class="text-black">{{ $t('extra_video_analysis_price') }}</span>
                                        </div>
                                    </span>
                                    <SingleToggle class="border-0" :active="webinarToggle" @update:active="handleToggle" />
                                </div>
                            </div>
                        </div>

                        <!-- CTA -->
                        <div v-if="!hasWebinar() || !webinarToggle" class="px-6 pb-6">
                            <button
                                type="button"
                                class="group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-800 px-5 py-3 text-sm font-semibold text-white shadow-md hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                                @click="$emit('add-to-cart')"
                            >
                                <span>{{ ctaText }}</span>
                                <i class="fi fi-rs-arrow-right flex align-middle text-white" />
                            </button>
                        </div>

                        <!-- Webinar CTA -->
                        <div v-if="hasWebinar() && webinarToggle" class="px-6 pb-6">
                            <button
                                type="button"
                                class="group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue px-5 py-3 text-sm font-semibold text-white shadow-md hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                                @click="
                                    () => {
                                        $emit('add-webinar-to-cart');
                                    }
                                "
                            >
                                <span>{{ webinarText }}</span>
                                <i class="fi fi-rs-arrow-right flex align-middle text-white" />
                            </button>
                        </div>
                        <p class="my-3 text-center text-xs text-slate-500">{{ ctaSubtext }}</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import FeatureItem from './FeatureItem.vue';
import PriceRow from './PriceRow.vue';
import InfoText from '../dashboard/InfoText.vue';
import SingleToggle from '../form/SingleToggle.vue';
import Toggle from '../form/Toggle.vue';

export default {
    name: 'ProductIntroSection',
    components: {
        Toggle,
        SingleToggle,
        InfoText,
        FeatureItem,
        PriceRow
    },
    props: {
        subheading: {
            type: String,
            default: ''
        },
        heading: {
            type: String,
            required: true
        },
        features: {
            type: Array,
            required: true
        },
        productGroup: {
            type: Object,
            required: true
        },
        selectedProductVariant: {
            type: Object,
            default: null
        },
        sortedProductVariants: {
            type: Array,
            default: () => []
        },
        pricingTitle: {
            type: String,
            required: true
        },
        pricingSubtitle: {
            type: String,
            default: 'Wählen Sie Ihre Laufzeit!'
        },
        ctaText: {
            type: String,
            required: true
        },
        ctaSubtext: {
            type: String,
            default: 'Sofort nach Buchung in Ihrem HKCM‑Dashboard und in unserer App abrufbar.'
        },
        webinarText: {
            type: String,
            default: 'Schriftliche + Videoanalyse abonnieren'
        }
    },
    emits: ['select-product', 'add-to-cart', 'add-webinar-to-cart'],
    data() {
        return {
            infoText:
                'Mit dieser Option erhalten Sie von Montag bis Freitag zusätzlich zu den schriftlichen Updates exklusive Videoanalysen zu allen Werten des Pakets (+BTC & ETH) sowie Sonderanalysen zu Dogecoin, SLP und Virtuals Protocol.\n' +
                'Zusätzlicher Preis pro Monat +9,95€ (+Paketpreis je nach Abo)\n' +
                'Gegebenenfalls ein Icon für die Videoanalyse zur Abgrenzung\n' +
                'Fonts: Altcoin-Paket ohne Videoanalyse abonnieren (dezentere Farbe - Grau) // Altcoin-Paket mit Videoanalyse abonnieren (HKCM-Blau)',
            webinarToggle: true
        };
    },
    methods: {
        getAttributeByType(productVariant, type) {
            return productVariant.productAttributeValues.data.find((productAttributeValue) => {
                return productAttributeValue.meta.type === type;
            });
        },
        hasWebinar() {
            return this.productGroup?.products?.data.some((products) => {
                return products?.tags?.data.some((tags) => {
                    return tags?.name === 'product-type:webinar';
                });
            });
        },
        handleToggle() {
            this.webinarToggle = !this.webinarToggle;
        }
    }
};
</script>
